import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/states";
import { cmsCall } from "@/lib/cms";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { readPending, clearPending, placePaidOrder } from "@/lib/payment";

/** Retourpagina van Mollie: status ophalen en daarna de CRM-order plaatsen. */
const Betaling = () => {
  const { reference } = useParams();
  const { t } = useTranslation();
  const { bestellen } = useAuth();
  const { clear } = useCart();
  const navigate = useNavigate();
  const [message, setMessage] = useState("checking");
  const [error, setError] = useState("");
  const busy = useRef(false);

  const run = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const betaling = await cmsCall("GET", `/betalingen/${reference}`);
      if (betaling.status !== "paid") {
        if (["canceled", "expired", "failed"].includes(betaling.status)) {
          clearPending(reference);
          setMessage("failed");
        } else {
          setMessage("checking");
          setTimeout(() => {
            busy.current = false;
            run();
          }, 3000);
        }
        return;
      }
      setMessage("placing");
      const pending = readPending(reference);
      if (!pending) {
        setError(t("errors.generic"));
        return;
      }
      const result = await placePaidOrder({ bestellen, pending, betaling, t });
      clearPending(reference);
      clear();
      navigate(`/besteld/${encodeURIComponent(result.ordernummer)}`, {
        replace: true,
        state: { result, lines: pending.lines, opmerking: pending.opmerking, referentie: pending.referentie },
      });
    } catch (e) {
      setError(e.message || t("errors.generic"));
    }
  }, [reference, bestellen, clear, navigate, t]);

  useEffect(() => {
    run();
  }, [run]);

  return (
    <div className="mx-auto max-w-xl px-4 py-16 sm:px-6 lg:px-8" data-testid="payment-return-page">
      <Card className="p-8">
        {error ? (
          <ErrorState message={error} onRetry={() => { busy.current = false; run(); }} />
        ) : message === "failed" ? (
          <>
            <p className="text-sm" data-testid="payment-failed-message">{t("payment.failed")}</p>
            <Button className="mt-5" onClick={() => navigate("/bestellen")} data-testid="payment-back-button">
              {t("payment.backToCheckout")}
            </Button>
          </>
        ) : (
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span data-testid="payment-status-message">
              {message === "placing" ? t("payment.placing") : t("payment.checking")}
            </span>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Betaling;
